import React, {
  useMemo,
  useState,
} from 'react';
import type {
  MouseEvent,
} from 'react';
import type {
  Entry,
} from 'contentful';
import {
  getStyles,
} from './Banner.styles';

type BannerProps = {
  entry: Entry;
};

type BannerFields = {
  text: string;
  linkText: string;
  linkHref: string;
  textColor: string;
  backgroundColor: string;
  canBeDismissed: boolean;
}

const Banner = (props: BannerProps): JSX.Element => {
  const {
    entry,
  } = props;
  const {
    fields,
  } = entry;

  const {
    text,
    linkText,
    linkHref,
    textColor,
    backgroundColor,
    canBeDismissed,
  } = fields as BannerFields;

  const isLinkPresent = !!linkText;

  const [closed, setClosed] = useState(false);

  const handleDismiss = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setClosed(true);
  };

  const styles = getStyles();

  const textStyles = useMemo(() => {
    if (!textColor) return styles.text;

    return {
      ...styles.text,
      color: textColor,
    };
  }, [textColor]);

  const bannerStyles = useMemo(() => {
    const style = styles.banner;

    if (closed) style.display = 'none';
    if (backgroundColor) style.backgroundColor = backgroundColor;

    return style;
  }, [closed, backgroundColor]);

  const dismissStyles = useMemo(() => {
    if (!textColor) return styles.dismiss;

    return {
      ...styles.dismiss,
      color: textColor,
    };
  }, [textColor]);

  if (!entry) return null;

  const Element = isLinkPresent ? 'a' : 'div';

  return (
    <Element
      href={linkHref}
      style={bannerStyles}
    >
      <p style={textStyles}>
        {text}
        &nbsp;
        {isLinkPresent && (
          <b style={styles.link}>
            {linkText}
          </b>
        )}
      </p>

      {canBeDismissed && (
        <button
          style={dismissStyles}
          onClick={handleDismiss}
          aria-label='close'
        >
          &times;
        </button>
      )}
    </Element>
  );
};

export default Banner;
